/*
 * FALLBACK ONLY: reached solely from the legacy branch of firstPass, when no
 * aligner/WhisperX helper answers (phones). Kept working per project
 * constraint — do not extend.
 */

/**
 * Duplicate resolution — the other half of gap fill's insert-only safety policy.
 *
 * WHY THIS EXISTS
 * ---------------
 * `services/legacy/gapFill.ts` may insert a word and may never delete one, deliberately
 * (read its header: an inserted word is a `pending` row a human rejects in one
 * tap, a deleted word is gone). That asymmetry is right, and it has a cost the
 * user has been reporting ever since gap fill started working:
 *
 *   the transcript now contains the word TWICE.
 *
 * The mechanism is exactly the one gap fill was built for, run to its end. The
 * first pass mis-placed a word — laid it onto audio it was never spoken on —
 * and left the audio where it WAS spoken bare. Gap fill finds that bare audio,
 * hears the word, and inserts it in the right place. Both copies now exist: the
 * new one on the right audio, the old one still sitting on somebody else's.
 * Measured on the user's 73-segment sample: "انا اسمي" inserted at 3.56–4.16 s
 * while the stale labels for the same words linger elsewhere.
 *
 * Left alone the stale copy is `pending` — indistinguishable, in the Verify
 * queue, from a real word waiting to be judged. It plays as drift, and until now
 * a human who recognised it had no way to say "this row should not exist".
 *
 * WHAT THIS DOES
 * --------------
 * Finds label runs that appear twice within {@link DEFAULT_WINDOW_SEC}, decides
 * whether one of the two copies is CLEARLY less plausible where it sits, and if
 * so marks that copy `verify: 'incorrect'` with no correction and a
 * {@link RemovalSuggestion} attached, so it lands in the Correct queue as a
 * one-tap suggested removal (see CorrectPage). Confidence drops to
 * {@link DUPLICATE_CONFIDENCE} so it sorts with the other things worth doubting.
 *
 * It never deletes. Outright deletion of a duplicate stays where it already is,
 * in `burstResegment.findDuplicatedCrams`, which requires overwhelming evidence
 * (every item under 0.12 s AND an exact label-sequence copy nearby occupying at
 * least twice the audio). That rule is untouched.
 *
 * WHY IT IS THIS CONSERVATIVE
 * ---------------------------
 * People repeat themselves. "لا لا", a name said twice, a corrected false start
 * — all of these are two genuine copies of one label a few seconds apart, and
 * flagging either of them would be worse than the bug: it teaches the user that
 * the suggestion means nothing. So a repeat is NOT evidence. The evidence is
 * POSITIONAL IMPLAUSIBILITY — this copy's span does not fit the label (its
 * duration is impossible for what it claims to be, or most of its span is not
 * even on speech) WHILE the other copy's does. Two genuine repeats both fit
 * their audio, score within a hair of each other, and are left alone; the four
 * hard gates in {@link findDuplicateSuggestions} all have to open.
 */

/*
 * No `../../db` import any more: the only storage entry point this module had
 * was `flagDuplicatesForSegments`, which had no callers and is gone. What
 * survives is pure analysis over in-memory rows; `clipQa` persists the result.
 */
import { round3 } from '../../lib/num';
import { foldForCompare, isFillerLabel } from '../../lib/labelText';
import { isSegmentResolved, type RemovalSuggestion, type Segment } from '../../types';
import { extractMono, formatTimecode } from '../audioSlicer';
import { detectOnsets, type OnsetCandidate } from '../onsetDetect';
import { note } from '../pipelineLog';
import { computeSpeechEnvelope, type SpeechEnvelope } from '../vad';
import { isPathological, type ResegmentItem } from './burstResegment';
import { expectedDurationRange } from './onsetAlign';

/* ------------------------------------------------------------------ */
/* Tunables                                                            */
/* ------------------------------------------------------------------ */

/**
 * How far apart two copies of a label may sit and still be one duplication.
 *
 * The mis-placement gap fill repairs is local: the first pass slid a word onto a
 * neighbouring burst, not to the other end of the recording. Beyond a few
 * seconds "the same word twice" is just vocabulary.
 */
export const DEFAULT_WINDOW_SEC = 6;

/**
 * How much plausibility the stale copy must give up before it is flagged.
 *
 * The score below runs 0..1 and two genuine repeats land within ~0.05 of each
 * other (both on speech, both plausibly long). 0.18 is far outside that and
 * still comfortably inside the real case, where the stale copy typically fails a
 * duration check outright (a 0.4-point swing on its own).
 */
const SCORE_MARGIN = 0.18;

/** The surviving copy must be this plausible before its rival is doubted. */
const KEEP_MIN_SPEECH_FRAC = 0.65;

/** ...and must itself sit comfortably inside the syllable model's window. */
const KEEP_MAX_DURATION_PENALTY = 0.15;

/** ...and the doubted copy must be at least this bad on one of the two axes. */
const STALE_MAX_SPEECH_FRAC = 0.6;

/** A flux onset this close to a segment's start counts as supporting it. */
const ONSET_TOL_SEC = 0.07;

/**
 * Total letters a matched run must carry.
 *
 * One short function word ("و", "في") repeating twice in six seconds is not
 * evidence of anything; it is how the language works.
 */
const MIN_RUN_LETTERS = 3;

/**
 * Confidence a suggested removal is left with.
 *
 * Below `QA_FAILED_CONFIDENCE` (0.3) on purpose: a clip QA failed to recognise
 * is a label that might still be right, and this is a label the pipeline
 * believes is on the wrong audio entirely.
 */
export const DUPLICATE_CONFIDENCE = 0.1;

/* ------------------------------------------------------------------ */
/* Context                                                             */
/* ------------------------------------------------------------------ */

/** The acoustic evidence the resolver judges positions against. */
export interface DuplicateContext {
  env: SpeechEnvelope;
  onsets: readonly OnsetCandidate[];
}

/**
 * Measure a decoded file once. Cheap enough to do per task (one VAD pass and one
 * flux pass over the whole file), and it keeps the resolver runnable on stored
 * data without a pipeline around it — which is how its verdicts get reviewed
 * before they are ever written.
 */
export function buildDuplicateContext(buffer: AudioBuffer): DuplicateContext {
  const mono = extractMono(buffer, 0, buffer.duration);
  const env = computeSpeechEnvelope(mono, buffer.sampleRate);
  const { onsets } = detectOnsets(mono, buffer.sampleRate);
  return { env, onsets };
}

/* ------------------------------------------------------------------ */
/* Scoring one side of a duplication                                   */
/* ------------------------------------------------------------------ */

/**
 * How well a group of segments fits the audio underneath it.
 *
 * The duration term is CONTINUOUS rather than a count of outright-impossible
 * items, and that is the difference between this test working and not working on
 * real data. Measured on the user's 73-segment sample: the VAD calls 96% of
 * every dense burst speech, so `speechFrac` barely separates a stale label from
 * a real one — while a stale copy is almost always a SQUEEZED one (the first
 * pass had to fit it somewhere it did not belong), and squeezing shows up as a
 * duration a hair under the syllable model's floor, not as an impossibility. A
 * binary "impossible" count scored the 0.11 s "انه" at 33.32 identically to the
 * 0.80 s one at 30.36 that it duplicates.
 */
export interface PositionScore {
  /** Fraction of the group's span the VAD calls speech. */
  speechFrac: number;
  /** Mean shortfall against the label's plausible duration window, 0..1. */
  durationPenalty: number;
  /** Fraction of its items the rest of the pipeline already calls pathological. */
  pathologicalFrac: number;
  /** Fraction of its items whose start sits on a spectral onset. */
  onsetFrac: number;
  /** The mix. 0..1, higher is more plausible. */
  score: number;
}

/** Same shape of penalty `onsets.ts` charges internally, recomputed per item. */
function durationPenalty(seg: Pick<Segment, 'heard' | 'kind'>, durSec: number): number {
  const { minSec, maxSec } = expectedDurationRange(seg.heard, seg.kind);
  if (durSec < minSec) return (minSec - durSec) / minSec;
  if (durSec > maxSec) return (durSec - maxSec) / maxSec;
  return 0;
}

function scorePosition(
  segs: readonly Segment[],
  ctx: DuplicateContext,
): PositionScore {
  const { env, onsets } = ctx;
  const hop = env.hopSec;
  let frames = 0;
  let speech = 0;
  let penalty = 0;
  let pathological = 0;
  let onOnset = 0;

  for (const s of segs) {
    const from = Math.max(0, Math.floor(s.startSec / hop));
    const to = Math.min(env.mask.length, Math.ceil(s.endSec / hop));
    for (let i = from; i < to; i++) {
      frames++;
      if (env.mask[i]) speech++;
    }
    penalty += Math.min(1, durationPenalty(s, s.endSec - s.startSec));
    // The pipeline's OWN definition of a span that cannot be what it claims —
    // under 0.12 s, or a short word running past a second. Reused rather than
    // restated so this stage cannot drift away from the rest of the app.
    if (isPathological(s)) pathological++;
    if (onsets.some((o) => o.source === 'flux' && Math.abs(o.timeSec - s.startSec) <= ONSET_TOL_SEC)) {
      onOnset++;
    }
  }

  const n = Math.max(1, segs.length);
  const speechFrac = frames > 0 ? speech / frames : 0;
  const durationPenaltyMean = penalty / n;
  const onsetFrac = onOnset / n;
  return {
    speechFrac: round3(speechFrac),
    durationPenalty: round3(durationPenaltyMean),
    pathologicalFrac: round3(pathological / n),
    onsetFrac: round3(onsetFrac),
    score: round3(0.4 * speechFrac + 0.45 * (1 - durationPenaltyMean) + 0.15 * onsetFrac),
  };
}

/* ------------------------------------------------------------------ */
/* Finding duplications                                                */
/* ------------------------------------------------------------------ */

/** One resolved duplication: which copy loses, which one it lost to, and why. */
export interface DuplicateFinding {
  /** Segments to flag, in time order. */
  staleIds: string[];
  staleIndices: number[];
  staleLabels: string[];
  staleStartSec: number;
  staleEndSec: number;
  stale: PositionScore;
  /** The copy judged to be in the right place. */
  keepIds: string[];
  keepIndices: number[];
  keepStartSec: number;
  keepEndSec: number;
  keep: PositionScore;
  /** Words in the matched run. */
  runLength: number;
  /** Human-facing, rendered on the Correct card. */
  hint: string;
  /** Machine-facing, for the run log. */
  evidence: string;
}

/** One segment's folded tokens, or `null` when it may not take part. */
function tokensOf(seg: Segment): string[] | null {
  // Only real words. Fillers ("اه"), noise descriptions and `[unclear]`
  // placeholders repeat by their nature and carry no positional information.
  if (seg.kind !== 'word') return null;
  if (isFillerLabel(seg.heard)) return null;
  const folded = foldForCompare(seg.heard);
  if (!folded) return null;
  const toks = folded.split(' ').filter(Boolean);
  return toks.length > 0 ? toks : null;
}

function lettersIn(tokens: readonly string[]): number {
  return tokens.reduce((n, t) => n + t.length, 0);
}

/**
 * Which segments duplicate which, and which copy is in the wrong place.
 *
 * Pure: reads the segments and the acoustic context, writes nothing. Runs
 * identically inside the pipeline and against a task already sitting in
 * IndexedDB, which is the only honest way to review what it would do.
 */
function findDuplicateSuggestions(
  segments: readonly Segment[],
  ctx: DuplicateContext,
  options: { windowSec?: number } = {},
): DuplicateFinding[] {
  const windowSec = options.windowSec ?? DEFAULT_WINDOW_SEC;
  const segs = [...segments].sort((a, b) => a.startSec - b.startSec);
  const n = segs.length;
  if (n < 2) return [];

  /* -- flatten to a token stream, remembering who owns each token ---- */
  const tokens: string[] = [];
  const owner: number[] = [];
  const tokenSpan: Array<[number, number] | null> = [];
  for (let i = 0; i < n; i++) {
    const toks = tokensOf(segs[i]);
    if (!toks) {
      tokenSpan.push(null);
      continue;
    }
    tokenSpan.push([tokens.length, tokens.length + toks.length]);
    for (const t of toks) {
      tokens.push(t);
      owner.push(i);
    }
  }

  /*
   * Candidate runs: maximal stretches of the token stream that appear twice.
   * Matching on TOKENS rather than whole labels is what lets a multi-word
   * insertion ("انا اسمي", one row) line up against two single-word stale rows.
   */
  const candidates: Array<{ a: number; b: number; len: number }> = [];
  const T = tokens.length;
  for (let a = 0; a < T; a++) {
    for (let b = a + 1; b < T; b++) {
      if (tokens[a] !== tokens[b]) continue;
      // Not maximal to the left — the longer run starting one token earlier
      // already covers this pair.
      if (a > 0 && b > 0 && tokens[a - 1] === tokens[b - 1]) continue;
      let len = 0;
      while (a + len < T && b + len < T && tokens[a + len] === tokens[b + len] && b > a + len) len++;
      if (len > 0) candidates.push({ a, b, len });
    }
  }
  // Longest first: a three-word duplication is stronger evidence than the
  // one-word duplication nested inside it, and claims its segments first.
  candidates.sort((x, y) => y.len - x.len);

  const spoken = new Set<number>(); // segments already part of a finding
  const findings: DuplicateFinding[] = [];

  for (const cand of candidates) {
    if (lettersIn(tokens.slice(cand.a, cand.a + cand.len)) < MIN_RUN_LETTERS) continue;

    const left = segmentRangeFor(cand.a, cand.len, owner, tokenSpan);
    const right = segmentRangeFor(cand.b, cand.len, owner, tokenSpan);
    if (!left || !right) continue; // a run that only covers part of a row
    if (left.to > right.from) continue; // overlapping or interleaved

    const leftSegs = segs.slice(left.from, left.to);
    const rightSegs = segs.slice(right.from, right.to);
    // A segment may take part in at most one finding: the longest run wins, and
    // nothing gets flagged twice on the strength of a sub-run of itself.
    if ([...range(left.from, left.to), ...range(right.from, right.to)].some((i) => spoken.has(i))) continue;

    // Gate 1 — the two copies are near each other in TIME, not just in the list.
    if (Math.abs(rightSegs[0].startSec - leftSegs[0].startSec) > windowSec) continue;

    const leftScore = scorePosition(leftSegs, ctx);
    const rightScore = scorePosition(rightSegs, ctx);
    const leftIsStale = leftScore.score < rightScore.score;
    const staleSegs = leftIsStale ? leftSegs : rightSegs;
    const keepSegs = leftIsStale ? rightSegs : leftSegs;
    const staleScore = leftIsStale ? leftScore : rightScore;
    const keepScore = leftIsStale ? rightScore : leftScore;

    // Gate 2 — never re-interpret human work. Only an untouched `pending` row
    // may be turned into a suggested removal.
    if (staleSegs.some((s) => s.verify !== 'pending' || isSegmentResolved(s) || s.correctedText)) continue;

    // Gate 3 — the loser is CLEARLY worse, not merely worse.
    if (keepScore.score - staleScore.score < SCORE_MARGIN) continue;

    // Gate 4 — positional implausibility, the thing that separates a stale copy
    // from a speaker repeating themselves. A margin alone is not enough: the
    // loser has to be somewhere it could not have been said (a span the pipeline
    // itself calls pathological, a duration well under the syllable model's
    // floor, or a span that is mostly not even speech) WHILE the winner fits
    // where it sits. Two genuine repeats both fit, and neither is touched.
    const staleIsImplausible =
      staleScore.pathologicalFrac > 0 ||
      staleScore.durationPenalty > 0.5 ||
      staleScore.speechFrac < STALE_MAX_SPEECH_FRAC;
    const keepIsPlausible =
      keepScore.pathologicalFrac === 0 &&
      keepScore.durationPenalty <= KEEP_MAX_DURATION_PENALTY &&
      keepScore.speechFrac >= KEEP_MIN_SPEECH_FRAC;
    if (!staleIsImplausible || !keepIsPlausible) continue;

    for (const i of [...range(left.from, left.to), ...range(right.from, right.to)]) spoken.add(i);

    const words = staleSegs.map((s) => s.heard).join(' ').trim();
    const staleSpanSec = staleSegs[staleSegs.length - 1].endSec - staleSegs[0].startSec;
    const keepSpanSec = keepSegs[keepSegs.length - 1].endSec - keepSegs[0].startSec;
    const why =
      staleScore.pathologicalFrac > 0 || staleScore.durationPenalty > 0.5
        ? `${staleSpanSec.toFixed(2)}s is not a plausible length for it, against ${keepSpanSec.toFixed(2)}s for the other copy`
        : `${Math.round(staleScore.speechFrac * 100)}% of it is speech, against ${Math.round(keepScore.speechFrac * 100)}% for the other copy`;

    findings.push({
      staleIds: staleSegs.map((s) => s.id),
      staleIndices: staleSegs.map((s) => s.index),
      staleLabels: staleSegs.map((s) => s.heard),
      staleStartSec: staleSegs[0].startSec,
      staleEndSec: staleSegs[staleSegs.length - 1].endSec,
      stale: staleScore,
      keepIds: keepSegs.map((s) => s.id),
      keepIndices: keepSegs.map((s) => s.index),
      keepStartSec: keepSegs[0].startSec,
      keepEndSec: keepSegs[keepSegs.length - 1].endSec,
      keep: keepScore,
      runLength: cand.len,
      hint: `“${words}” is also labelled at ${formatTimecode(keepSegs[0].startSec)}, where it fits the audio better — remove this copy?`,
      evidence:
        `dup “${words}” ${fmtSpan(staleSegs)} (score ${staleScore.score.toFixed(2)}, ` +
        `speech ${Math.round(staleScore.speechFrac * 100)}%, duration penalty ${staleScore.durationPenalty.toFixed(2)}) ` +
        `loses to ${fmtSpan(keepSegs)} (score ${keepScore.score.toFixed(2)}, ` +
        `speech ${Math.round(keepScore.speechFrac * 100)}%, duration penalty ${keepScore.durationPenalty.toFixed(2)}): ${why}`,
    });
  }

  return findings.sort((a, b) => a.staleStartSec - b.staleStartSec);
}

/** The `[from, to)` segment range a token run covers, or null if it splits one. */
function segmentRangeFor(
  at: number,
  len: number,
  owner: readonly number[],
  tokenSpan: ReadonlyArray<[number, number] | null>,
): { from: number; to: number } | null {
  const first = owner[at];
  const last = owner[at + len - 1];
  const firstSpan = tokenSpan[first];
  const lastSpan = tokenSpan[last];
  if (!firstSpan || !lastSpan) return null;
  // A run that starts or ends mid-label would mean flagging half a row, which
  // is not a thing this app can express. Skip it rather than round.
  if (firstSpan[0] !== at || lastSpan[1] !== at + len) return null;
  // Every segment in between must be fully covered too (it is, by construction,
  // as long as the owners are contiguous).
  for (let i = first; i <= last; i++) if (!tokenSpan[i]) return null;
  return { from: first, to: last + 1 };
}

function* range(from: number, to: number): Generator<number> {
  for (let i = from; i < to; i++) yield i;
}

function fmtSpan(segs: readonly Segment[]): string {
  return `${segs[0].startSec.toFixed(2)}–${segs[segs.length - 1].endSec.toFixed(2)}`;
}

/* ------------------------------------------------------------------ */
/* Applying a finding                                                  */
/* ------------------------------------------------------------------ */

/** The patch a finding makes to one stale segment. Pure. */
function suggestRemoval(seg: Segment, finding: DuplicateFinding): Segment {
  const suggestion: RemovalSuggestion = {
    reason: 'duplicate',
    hint: finding.hint,
    counterpartId: finding.keepIds[0],
    at: Date.now(),
  };
  return {
    ...seg,
    // Incorrect with NO correction: `isSegmentResolved` is false, so it lands in
    // the Correct queue instead of sitting in Verify looking like real work.
    verify: 'incorrect',
    confidence: Math.min(seg.confidence, DUPLICATE_CONFIDENCE),
    suggestedRemoval: suggestion,
  };
}

export interface DuplicateStats {
  /** Duplications the resolver acted on. */
  resolved: number;
  /** Segments marked as suggested removals. */
  flagged: number;
  /** One line per resolution, for the run log. */
  findings: string[];
}

const EMPTY_STATS = (): DuplicateStats => ({ resolved: 0, flagged: 0, findings: [] });

/**
 * Pipeline entry point: flag the stale copies in an in-flight QA item list.
 *
 * Mutates `items` (marking the rows dirty) rather than writing, so the flags
 * land in the QA loop's own final transaction along with everything else it did.
 */
export function resolveDuplicatesInItems(
  items: ResegmentItem[],
  ctx: DuplicateContext,
  options: { windowSec?: number } = {},
): DuplicateStats {
  const stats = EMPTY_STATS();
  const live = items.filter((i) => !i.removed);
  if (live.length < 2) return stats;

  const findings = findDuplicateSuggestions(live.map((i) => i.seg), ctx, options);
  const byId = new Map(live.map((i) => [i.seg.id, i]));
  for (const finding of findings) {
    stats.resolved++;
    stats.findings.push(finding.evidence);
    note(`duplicate resolved: ${finding.evidence}`);
    for (const id of finding.staleIds) {
      const item = byId.get(id);
      if (!item) continue;
      item.seg = suggestRemoval(item.seg, finding);
      item.dirty = true;
      stats.flagged++;
    }
  }
  return stats;
}

/*
 * DELETED in the restructure, both with zero callers:
 *  - `flagDuplicatesForSegments`, a storage entry point nothing ever reached;
 *  - `export { clearRemovalSuggestion }`, pure laundering — CorrectPage imports
 *    that undo directly from `../../db`, which is where it lives.
 */
